import type { SanityImage } from '@repo/models/gatsby-graphql-types';
import { sanitizeMetaText } from '@repo/utils/sanitizeMetaText';

let baseUrl =
  process.env.CONTEXT === 'production'
    ? process.env.URL
    : process.env.DEPLOY_PRIME_URL;

if (!baseUrl) {
  if (typeof window !== 'undefined') {
    baseUrl = new URL(window.location.origin)?.origin;
  }
}

// True when the value already includes a host (scheme://... or //host),
// so we can emit it as-is instead of prepending the brand baseUrl.
// e.g. "https://www.invisiblefence.com/..." -> true; "/resource-center/articles" -> false
const isAbsoluteUrl = (value: string) =>
  /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(value);

export const getSanityImageCropUrl = (
  image?:
    | string
    | {
        asset?: { url?: string | null } | null;
        hotspot?: { x?: number | null; y?: number | null } | null;
      }
    | null
): string | undefined => {
  if (!image) return undefined;
  if (typeof image === 'string') return image;

  let url = image?.asset?.url ? `${image.asset.url}?w=1200&h=630` : undefined;
  if (url && image.hotspot) {
    // if we've got a hotspot, use it
    const { x } = image.hotspot;
    const { y } = image.hotspot;
    url += `&fit=crop&crop=focalpoint&fp-x=${x}&fp-y=${y}`;
  }

  return url;
};

const brands = [
  {
    name: 'Kurgo',
    baseUrl,
    logo: `${baseUrl}/images/kurgo_social.png`,
    seo: {
      title: 'Kurgo: High Quality Outdoor Adventure Dog Gear Products',
      description:
        'Kurgo is a leading supplier of quality dog travel supplies, seat covers, harnesses & accessories for active dogs. Click to learn more about our dog accessories.',
    },
    twitter: '@Kurgo',
  },
  {
    name: 'SportDOG',
    baseUrl,
    logo: `${baseUrl}/images/opengraph-fallback.png`,
    seo: {
      title:
        'Dog Training Collars for Hunting Dogs, Gun Dogs, Sporting Dogs - SportDOG ®',
      description:
        'Hunting dog gear designed by sportsmen. Sporting dog training, tracking, and hunting information to get the most out of your next hunt.',
    },
    twitter: '@SportDOGBrand',
  },
  {
    name: 'InvisibleFence',
    baseUrl,
    logo: `${baseUrl}/images/opengraph-fallback.png`,
    seo: {
      title: 'Invisible Fence',
      description: 'Invisible Fence',
    },
    twitter: '@invisiblefence',
  },
  {
    name: 'PetSafe',
    baseUrl,
    logo: `${baseUrl}/images/opengraph-fallback.png`,
    seo: {
      title: 'PetSafe® | Dog Fences | Feeders | Collars | Cat Litter Boxes',
      description:
        "PetSafe® Brand is the world's leader in containment, training, and lifestyle solutions to give pet owners more great moments with their pets.",
    },
    twitter: '@petsafe',
  },
];

interface MetaTagProps {
  title?: string;
  description?: string;
  url?: string;
  image?: SanityImage | string;
  type?: string;
  article?: {
    published_time?: string;
    author?: string;
    tag?: string[];
  };
  'og:price:currency'?: string;
  'og:price:amount'?: string | number;
}

export function getBrandSeoDefaults(
  brand: string
): { title: string; description: string } | undefined {
  const activeBrand = brands.find(
    b => b.name.toLowerCase() === brand.toLowerCase()
  );

  return activeBrand?.seo;
}

const createOpenGraphTags = (meta: MetaTagProps, brand: string) => {
  const activeBrand = brands.find(
    b => b.name.toLowerCase() === brand.toLowerCase()
  );

  const openGraphImageUrl = image =>
    getSanityImageCropUrl(image) ?? activeBrand.logo;

  const title =
    sanitizeMetaText(meta.title) ?? activeBrand.seo.title;
  const description =
    sanitizeMetaText(meta.description) ?? activeBrand.seo.description;
  const {
    url = '/',
    image = activeBrand.logo,
    type = 'website',
  } = meta;

  const metaTags = [
    /*
     * OpenGraph
     */
    {
      property: 'og:image',
      content: openGraphImageUrl(image),
    },
    { property: 'og:image', content: `${activeBrand.logo}` },
    {
      property: 'og:url',
      content: isAbsoluteUrl(url)
        ? url
        : `${activeBrand.baseUrl ?? ''}${url}`,
    },
    {
      property: 'og:type',
      content: type,
    },
    {
      property: 'og:title',
      content: title,
    },
    {
      property: 'og:description',
      content: description,
    },
    /*
     * Twitter
     */
    {
      name: 'twitter:image',
      content: openGraphImageUrl(image),
    },
    { name: 'og:image', content: `${activeBrand.logo}` },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:domain',
      content: `${activeBrand.baseUrl}`,
    },
    {
      name: 'twitter:url',
      content: isAbsoluteUrl(url)
        ? url
        : `${activeBrand.baseUrl ?? ''}${url}`,
    },
    {
      name: 'twitter:title',
      content: title,
    },
    {
      name: 'twitter:description',
      content: description,
    },
    {
      name: 'twitter:site',
      content: activeBrand.twitter,
    },
  ];

  // Generates Article Structure (tags, published_at, etc.) based on meta.type
  // https://ogp.me/
  if (meta?.type === 'article' && meta?.article) {
    const articleDetails = meta.article;
    Object.keys(articleDetails).forEach(key => {
      if (key !== 'tag') {
        metaTags.push({
          name: `article:${key}`,
          content: articleDetails[key],
        });
      } else {
        articleDetails[key].forEach(tag => {
          metaTags.push({
            name: `article:tag`,
            content: tag,
          });
        });
      }
    });
  }

  return metaTags;
};

export default createOpenGraphTags;
